import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import type { ScheduledOrder, ScheduledOrdersPagination } from "~/features/scheduled/types";
import type { ScheduledWindowEdit } from "~/features/scheduled/components/ScheduledOrderEditModal";
import { fetchScheduledOrders, updateScheduledOrder } from "~/features/scheduled/api";
import { useLocationCode } from "~/shared/contexts/LocationCodeContext";
import { usePersistedFilter } from "~/shared/hooks/usePersistedFilter";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200] as const;
import { postOrdersToNav } from "~/features/posted-orders/api";
import { ApiError } from "~/shared/utils/api-error";
import { configureMsalInstance } from "~/services/api-client";
import { useTokenErrorHandler } from "~/shared/hooks/useTokenErrorHandler";
import { useServerSearch } from "~/shared/hooks/useServerSearch";
import type { Toast } from "~/features/orders/types";
import { ScheduledOrderTable } from "~/features/scheduled/components/ScheduledOrderTable";
import { Toast as ToastComponent } from "~/shared/components/Toast";
import { CenteredLoading } from "~/shared/components/CenteredLoading";
import { ErrorState } from "~/shared/components/ErrorState";
import { GlobalSearchBar } from "~/shared/components/GlobalSearchBar";

export default function ScheduledPage() {
  const { instance: msalInstance } = useMsal();
  const { selectedLocationCode } = useLocationCode();

  // State
  const [orders, setOrders] = useState<ScheduledOrder[]>([]);
  const allCitiesSeenRef = useRef<Set<string>>(new Set());
  const [allCities, setAllCities] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<typeof PAGE_SIZE_OPTIONS[number]>(PAGE_SIZE_OPTIONS[4]);
  const [pagination, setPagination] = useState<ScheduledOrdersPagination>({
    total: 0, page: 1, limit: PAGE_SIZE_OPTIONS[4], totalPages: 0,
  });
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'future'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  // Per-table column filters — Future table (persisted)
  const [futureCityFilter, setFutureCityFilter, futureCityFilterRef] = usePersistedFilter('sc:futureCity');
  const [futureOutcomeFilter, setFutureOutcomeFilter] = usePersistedFilter('sc:futureOutcome');
  const [futureWhiteGloveFilter, setFutureWhiteGloveFilter] = usePersistedFilter('sc:futureWhiteGlove');

  // Per-table column filters — Same Day table (persisted)
  const [todayCityFilter, setTodayCityFilter, todayCityFilterRef] = usePersistedFilter('sc:todayCity');
  const [todayOutcomeFilter, setTodayOutcomeFilter] = usePersistedFilter('sc:todayOutcome');
  const [todayWhiteGloveFilter, setTodayWhiteGloveFilter] = usePersistedFilter('sc:todayWhiteGlove');

  const selectedLocationCodeRef = useRef(selectedLocationCode);

  // Refs
  const hasLoadedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isLoadingRef = useRef(false);
  const isInitialMount = useRef(true);

  const { handleTokenError, resetTokenErrorRetry } = useTokenErrorHandler(msalInstance);

  function buildFilters() {
    const allCities = Array.from(new Set([
      ...futureCityFilterRef.current,
      ...todayCityFilterRef.current,
    ]));
    return {
      cities: allCities.length > 0 ? allCities : undefined,
      locationCodes: selectedLocationCodeRef.current ? [selectedLocationCodeRef.current] : undefined,
    };
  }

  function extractErrorMessage(err: unknown): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error) return err.message;
    return "Failed to load scheduled orders";
  }

  // Data loading
  const load = useCallback(async (reqPage?: number, reqLimit?: number) => {
    if (isLoadingRef.current) return;

    const p = reqPage ?? page;
    const lim = reqLimit ?? pageSize;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const { orders: scheduledOrders, pagination: pag, contextualCities } = await fetchScheduledOrders(
        p,
        lim,
        searchQueryRef.current ?? undefined,
        buildFilters()
      );
      setOrders(scheduledOrders);
      setPagination(pag);
      contextualCities.forEach(c => allCitiesSeenRef.current.add(c));
      setAllCities(Array.from(allCitiesSeenRef.current).sort((a, b) => a.localeCompare(b)));
      setPage(pag.page);
      setSelected({});
      setError(null);
      hasLoadedRef.current = true;
      resetTokenErrorRetry();
    } catch (err: unknown) {
      if (err instanceof Error && (err.name === "AbortError" || err.message.includes("cancel"))) return;
      if (handleTokenError(err, () => load(p, lim), () => { hasLoadedRef.current = false; isLoadingRef.current = false; })) return;

      const errorMessage = extractErrorMessage(err);

      setError(errorMessage);
      console.error("Failed to fetch scheduled orders:", err);
      setToast({ message: errorMessage, type: "error" });
      setTimeout(() => setToast(null), 5000);
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [handleTokenError, page, pageSize, resetTokenErrorRetry]);

  // Initialize on mount
  useEffect(() => {
    configureMsalInstance(msalInstance);
    load(1, PAGE_SIZE_OPTIONS[4]);
    return () => { abortControllerRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when page or pageSize changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    load(page, pageSize);
  }, [page, pageSize, load]);

  // Keep location ref in sync
  useEffect(() => { selectedLocationCodeRef.current = selectedLocationCode; }, [selectedLocationCode]);

  // Ref to always hold the latest load for filter/search triggers
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Re-fetch when location changes
  const prevLocationRef = useRef(selectedLocationCode);
  useEffect(() => {
    if (prevLocationRef.current === selectedLocationCode) return;
    prevLocationRef.current = selectedLocationCode;
    if (!hasLoadedRef.current) return;
    loadRef.current(1, pageSize);
  }, [selectedLocationCode, pageSize]);

  // Server-side search (debounced input + immediate Enter)
  const { searchQuery, searchQueryRef, handleSearchChange, handleSearchSubmit } = useServerSearch({
    onSearch: () => loadRef.current(1, pageSize),
  });

  // Per-table filter handlers — all client-side, no API reload needed
  const handleFutureCityFilterChange = useCallback((v: string[]) => {
    setFutureCityFilter(v);
    setTodayCityFilter(v);
    loadRef.current(1, pageSize);
  }, [pageSize, setFutureCityFilter, setTodayCityFilter]);
  const handleFutureOutcomeFilterChange = useCallback((v: string[]) => setFutureOutcomeFilter(v), [setFutureOutcomeFilter]);
  const handleFutureWhiteGloveFilterChange = useCallback((v: string[]) => setFutureWhiteGloveFilter(v), [setFutureWhiteGloveFilter]);

  const handleTodayCityFilterChange = useCallback((v: string[]) => {
    setTodayCityFilter(v);
    setFutureCityFilter(v);
    loadRef.current(1, pageSize);
  }, [pageSize, setTodayCityFilter, setFutureCityFilter]);
  const handleTodayOutcomeFilterChange = useCallback((v: string[]) => setTodayOutcomeFilter(v), [setTodayOutcomeFilter]);
  const handleTodayWhiteGloveFilterChange = useCallback((v: string[]) => setTodayWhiteGloveFilter(v), [setTodayWhiteGloveFilter]);

  // Use local date so "today" matches the user's calendar day (not UTC).
  const todayKey = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  const getOrderDateKey = (o: ScheduledOrder) => {
    const dateStr = o.confirmedDateAndWindow?.newDate ?? o.plannedDeliveryDateAndWindow?.newDate;
    return dateStr?.split("T")[0] ?? "";
  };

  const sameDayOrders = useMemo(() => orders.filter(o => getOrderDateKey(o) === todayKey), [orders, todayKey]);
  const futureOrders = useMemo(() => orders.filter(o => getOrderDateKey(o) > todayKey), [orders, todayKey]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  function toggle(id: string) { setSelected(s => ({ ...s, [id]: !s[id] })); }

  function selectAll(ids: string[]) {
    setSelected(prev => {
      const next: Record<string, boolean> = { ...prev };
      ids.forEach(id => { next[id] = true; });
      return next;
    });
  }

  function toggleDateFilter(filter: 'today' | 'future') {
    setDateFilter((prev) => (prev === filter ? 'all' : filter));
  }

  function selectToday() { selectAll(sameDayOrders.map(o => o._id)); }
  function selectFuture() { selectAll(futureOrders.map(o => o._id)); }
  function clearSelection() { setSelected({}); }

  function clearToday() {
    setSelected(prev => {
      const next = { ...prev };
      sameDayOrders.forEach(o => { delete next[o._id]; });
      return next;
    });
  }

  function clearFuture() {
    setSelected(prev => {
      const next = { ...prev };
      futureOrders.forEach(o => { delete next[o._id]; });
      return next;
    });
  }

  const postSelected = async () => {
    if (selectedIds.length === 0) {
      setToast({ message: 'Select at least one order', type: 'error' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    try {
      configureMsalInstance(msalInstance);
      setLoading(true);
      setError(null);

      // Get the orderId (SM number) for each selected order, not the MongoDB _id
      const orderIds = selectedIds
        .map(id => {
          const order = orders.find(o => o._id === id);
          return order?.orderId;
        })
        .filter((id): id is string => !!id); // Filter out undefined values

      if (orderIds.length === 0) {
        throw new Error('No valid order IDs found for selected orders');
      }

      console.log(`[Scheduled] Posting ${orderIds.length} orders to NAV:`, orderIds);

      // Call the API to post orders to NAV
      const result = await postOrdersToNav(orderIds);

      console.log(`[Scheduled] Posted ${result.successful} of ${result.totalAttempted} orders to NAV`);

      // Clear selection and reload orders
      setSelected({});
      hasLoadedRef.current = false;
      await load(page, pageSize);

      let msg: string;
      let toastType: 'success' | 'error';

      if (result.successful === 0 && result.errors?.length) {
        // Complete failure
        const reasons = result.errors.map(e => `${e.orderId}: ${e.reason}`).join('; ');
        msg = `Failed to post all ${result.totalAttempted} orders. ${reasons}`;
        toastType = 'error';
      } else if (result.failed > 0 && result.errors?.length) {
        // Partial success
        const failedIds = result.errors.map(e => e.orderId).join(', ');
        msg = `Posted ${result.successful} of ${result.totalAttempted} orders. Failed (${result.failed}): ${failedIds}`;
        toastType = 'error';
      } else if (result.failed > 0) {
        msg = `Posted ${result.successful} of ${result.totalAttempted} orders (${result.failed} failed)`;
        toastType = 'error';
      } else {
        msg = `Successfully posted ${result.successful} order${result.successful !== 1 ? 's' : ''} to NAV`;
        toastType = 'success';
      }
      setToast({ message: msg, type: toastType });
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      let errorMessage: string;
      if (err instanceof ApiError || err instanceof Error) {
        errorMessage = err.message;
      } else {
        errorMessage = 'Failed to post orders to NAV';
      }
      console.error('[Scheduled] Failed to post orders to NAV:', err);
      setToast({ message: errorMessage, type: 'error' });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  type OrderUpdateInput = {
    outcome: string;
    confirmedDate: string;
    confirmedWindow: ScheduledWindowEdit | null;
    comments: string;
    notes: string;
  };

  const buildScheduledOrderPayload = (updates: OrderUpdateInput): {
    outcome?: string;
    confirmedDeliveryDate?: string;
    confirmedWindow?: { start: number; end: number; label?: string; startMinutes?: number; endMinutes?: number };
    comments?: string;
    notes?: string;
  } => {
    const payload: ReturnType<typeof buildScheduledOrderPayload> = {};
    if (updates.outcome) payload.outcome = updates.outcome;
    if (updates.comments !== undefined) payload.comments = updates.comments;
    if (updates.notes !== undefined) payload.notes = updates.notes;

    const windowPayload = updates.confirmedWindow
      ? {
        start: updates.confirmedWindow.start,
        end: updates.confirmedWindow.end,
        label: updates.confirmedWindow.label,
        // Keep exact minutes so an unnamed/custom window is sent back unchanged
        ...(updates.confirmedWindow.startMinutes != null && updates.confirmedWindow.endMinutes != null && {
          startMinutes: updates.confirmedWindow.startMinutes,
          endMinutes: updates.confirmedWindow.endMinutes,
        }),
      }
      : null;

    if (updates.outcome === 'alternate_window' && windowPayload) {
      payload.confirmedWindow = windowPayload;
    } else {
      if (updates.confirmedDate) {
        const date = new Date(updates.confirmedDate);
        if (!Number.isNaN(date.getTime())) payload.confirmedDeliveryDate = date.toISOString();
      }
      if (windowPayload) payload.confirmedWindow = windowPayload;
    }
    return payload;
  };

  const handleUpdateOrder = async (orderId: string, updates: OrderUpdateInput) => {
    try {
      configureMsalInstance(msalInstance);
      const updateData = buildScheduledOrderPayload(updates);
      await updateScheduledOrder(orderId, updateData);
      hasLoadedRef.current = false;
      await load(page, pageSize);
      setToast({ message: 'Order updated successfully', type: 'success' });
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiError || err instanceof Error
        ? err.message
        : 'Failed to update scheduled order';
      setToast({ message: errorMessage, type: 'error' });
      setTimeout(() => setToast(null), 5000);
      console.error('Failed to update scheduled order:', err);
    }
  };

  const totalPages = pagination.totalPages;
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const displayCount = `${pagination.total} total orders`;

  function getPageNumbers(): Array<number | '…'> {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (page > 3) pages.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i);
    }
    if (page < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }

  function navigatePage(newPage: number) {
    if (newPage === page) return;
    setPage(newPage);
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--app-nav-header-height) + var(--app-nav-height))',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--neutral-gray)',
        padding: '4px 32px 0',
        boxSizing: 'border-box',
        overflow: 'hidden',
        zIndex: 1,
      }}
    >
      {/* Page header — always visible */}
      <div className="page-header" style={{ flexShrink: 0, marginBottom: 8 }}>
        <h1 className="page-title">🕐 Scheduled Orders</h1>
        <p className="page-subtitle">Scheduled orders for today and the future</p>
      </div>

      {/* Controls bar — always visible */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {selectedIds.length > 0 && (
          <span className="badge info">{selectedIds.length} selected</span>
        )}
        <button
          type="button"
          className={dateFilter === 'today' ? 'primary' : 'secondary'}
          onClick={() => toggleDateFilter('today')}
          disabled={sameDayOrders.length === 0}
          aria-pressed={dateFilter === 'today'}
        >
          Show Today's Orders
        </button>
        <button
          type="button"
          className={dateFilter === 'future' ? 'primary' : 'secondary'}
          onClick={() => toggleDateFilter('future')}
          disabled={futureOrders.length === 0}
          aria-pressed={dateFilter === 'future'}
        >
          Show Future Orders
        </button>
        <button className="primary" onClick={postSelected} disabled={selectedIds.length === 0}>Post Selected to NAV</button>
        <div style={{ flex: 1 }} />
        <GlobalSearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          placeholder="Search by SM#, patient, patient ID, postal, phone…"
        />
      </div>

      {/* Loading / error states */}
      {loading && orders.length === 0 && <CenteredLoading />}
      {error && !loading && (
        <ErrorState
          message={error}
          onRetry={() => { hasLoadedRef.current = false; load(page, pageSize); }}
        />
      )}

      {/* Scrollable tables zone */}
      {!(loading && orders.length === 0) && !error && (
        <div className="no-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {(() => {
            const showFuture = dateFilter === 'all' || dateFilter === 'future';
            const showToday = dateFilter === 'all' || dateFilter === 'today';
            const bothVisible = showFuture && showToday;
            return (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: bothVisible ? 10 : 0,
              }}>
                {showFuture && (
                  <div style={{ height: 350, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                    <ScheduledOrderTable
                      orders={futureOrders}
                      loading={loading}
                      selected={selected}
                      allSelected={futureOrders.length > 0 && futureOrders.every(o => selected[o._id])}
                      onToggle={toggle}
                      onSelectAll={selectFuture}
                      onClear={clearFuture}
                      title="Future"
                      emptyMessage="No future scheduled orders"
                      cityFilter={futureCityFilter}
                      onCityFilterChange={handleFutureCityFilterChange}
                      cityOptions={allCities}
                      outcomeFilter={futureOutcomeFilter}
                      onOutcomeFilterChange={handleFutureOutcomeFilterChange}
                      whiteGloveFilter={futureWhiteGloveFilter}
                      onWhiteGloveFilterChange={handleFutureWhiteGloveFilterChange}
                      onUpdateOrder={handleUpdateOrder}
                    />
                  </div>
                )}

                {showToday && (
                  <div style={{ height: 350, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                    <ScheduledOrderTable
                      orders={sameDayOrders}
                      loading={loading}
                      selected={selected}
                      allSelected={sameDayOrders.length > 0 && sameDayOrders.every(o => selected[o._id])}
                      onToggle={toggle}
                      onSelectAll={selectToday}
                      onClear={clearToday}
                      title="Same Day"
                      emptyMessage="No same day scheduled orders"
                      cityFilter={todayCityFilter}
                      onCityFilterChange={handleTodayCityFilterChange}
                      cityOptions={allCities}
                      outcomeFilter={todayOutcomeFilter}
                      onOutcomeFilterChange={handleTodayOutcomeFilterChange}
                      whiteGloveFilter={todayWhiteGloveFilter}
                      onWhiteGloveFilterChange={handleTodayWhiteGloveFilterChange}
                      onUpdateOrder={handleUpdateOrder}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Pinned pagination bar — always visible */}
      <div
        className="autocall-pagination"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
          padding: '10px 0',
          borderTop: '1px solid var(--border)',
          background: 'var(--neutral-gray)',
        }}
      >
        <span style={{ fontWeight: 500 }}>Orders per page:</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <button
              key={size}
              type="button"
              className={pageSize === size ? 'primary' : 'secondary'}
              onClick={() => { setPageSize(size); navigatePage(1); }}
              style={{ minWidth: 40 }}
            >
              {size}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            className="secondary"
            onClick={() => navigatePage(Math.max(1, page - 1))}
            disabled={!canPrev}
            style={{ minWidth: 36 }}
          >
            ‹
          </button>
          {getPageNumbers().map((pg, i, arr) =>
            pg === '…' ? (
              <span key={`ellipsis-${arr[i - 1]}-${arr[i + 1]}`} style={{ padding: '0 6px', color: 'var(--text-muted)' }}>…</span>
            ) : (
              <button
                key={pg}
                type="button"
                className={page === pg ? 'primary' : 'secondary'}
                onClick={() => navigatePage(pg)}
                style={{ minWidth: 36 }}
              >
                {pg}
              </button>
            )
          )}
          <button
            type="button"
            className="secondary"
            onClick={() => navigatePage(Math.min(totalPages, page + 1))}
            disabled={!canNext}
            style={{ minWidth: 36 }}
          >
            ›
          </button>
        </div>

        <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {displayCount}
        </span>

        <button
          type="button"
          className="secondary"
          onClick={() => load(page, pageSize)}
          aria-label="Refresh"
        >
          Refresh
        </button>
      </div>

      {toast && <ToastComponent toast={toast} />}
    </div>
  );
}
